import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  Card,
  Row,
  Col,
  Pagination,
  // Badge,
  Spinner,
} from "react-bootstrap";
import {
  User,
  Eye,
  Search,
  RefreshCw,
  SlidersHorizontal,
  MapPin,
  Hash,
} from "lucide-react";
import "../../assets/styles/UsersManagement.css";
import { allUsersAPI } from "../../services/userService";
import { faculties, userRoles } from "../../App";

function UsersManagement() {
  const navigate = useNavigate();

  // --- State ---
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [collegeFilter, setCollegeFilter] = useState("All");
  const [onlineCount, setOnlineCount] = useState(0);

  // Ref to prevent race conditions
  const abortControllerRef = useRef(null);

  // --- Main Fetch Function ---
  const fetchUsers = async (page = 1, currentSearch = search) => {
    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const params = {
        page,
        limit: 5,
        // Only send params if they are NOT "All" or empty
        search: currentSearch.trim() || undefined,
        role: roleFilter !== "All" ? roleFilter : undefined,
        status: statusFilter !== "All" ? statusFilter : undefined,
        faculty: collegeFilter !== "All" ? collegeFilter : undefined,
      };

      const response = await allUsersAPI(params); // Pass signal if your API supports it: { signal: abortControllerRef.current.signal }

      setOnlineCount(response.data.onlineCount || 0);

      if (response?.data?.users) {
        setUsers(response.data.users);
        setPagination(response.data.pagination);
      } else {
        setUsers([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching users:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Effect 1: Filters Change (Immediate Fetch) ---
  useEffect(() => {
    fetchUsers(1, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter, collegeFilter]);

  // --- Effect 2: Search Change (Debounced Fetch) ---
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(1, search);
    }, 600); // Wait 600ms after typing stops
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // --- Handlers ---
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchUsers(page, search);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const resetFilters = () => {
    setSearch("");
    setRoleFilter("All");
    setStatusFilter("All");
    setCollegeFilter("All");
    // Effects will trigger the fetch automatically
  };

  return (
    <div style={{ marginTop: "75px" }} className="users-page-container">
      <div className="container-fluid p-0">
        {/* === Header Section (Full) === */}
        <div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 px-3 px-md-5 pt-4 gap-3">
          {/* 1. Title & Counter (The New Design) */}
          <div className="d-flex align-items-center">
            <div className="icon-box me-3 flex-shrink-0 shadow-sm">
              <User size={24} className="text-primary" />
            </div>
            <div>
              <h3 className="fw-bold m-0 text-dark header-title">
                User Management
              </h3>
              <div className="d-flex align-items-center mt-1">
                <span className="text-muted small fw-medium me-2">
                  Total Members:
                </span>
                <span className="bg-primary-subtle text-primary border border-primary-subtle rounded-pill px-3">
                  {pagination.totalUsers}
                </span>
              </div>
              <div className="d-flex align-items-center mt-1">
                <span className="text-muted small fw-medium me-2">
                  Online :
                </span>
                <span className="bg-success-subtle text-success border border-success-subtle rounded-pill px-3">
                  {onlineCount}
                </span>
              </div>
            </div>
          </div>

          {/* 2. Search & Mobile Filter Toggle (Don't Remove This!) */}
          <div className="d-flex gap-2 w-100 w-md-auto">
            <div className="search-input-group flex-grow-1 w-md-auto">
              <Search className="search-icon" size={18} />
              <Form.Control
                type="text"
                placeholder="Search name, ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Button
              variant="white"
              className="d-md-none border shadow-sm d-flex align-items-center justify-content-center"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              style={{ width: "48px", height: "48px" }} // نفس ارتفاع الـ input
            >
              <SlidersHorizontal size={20} className="text-secondary" />
            </Button>
          </div>
        </div>

        {/* === Mobile Filters Collapsible === */}
        <div
          className={`filters-wrapper px-3 px-md-5 mb-4 ${showMobileFilters ? "d-block" : "d-none d-md-block"}`}
        >
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden bg-white">
            <Card.Body className="p-3 p-md-4">
              <Row className="g-2">
                <Col lg={3} xs={6}>
                  <Form.Select
                    className="custom-select"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="All">Role: All</option>
                    <option value={userRoles.student}>Student</option>
                    <option value={userRoles.supervisor}>Supervisor</option>
                  </Form.Select>
                </Col>
                <Col lg={3} xs={6}>
                  <Form.Select
                    className="custom-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="All">Status: All</option>
                    <option value="ACTIVE">Active</option>
                    <option value="BLOCKED">Blocked</option>
                  </Form.Select>
                </Col>
                <Col lg={3} xs={12}>
                  <Form.Select
                    className="custom-select"
                    value={collegeFilter}
                    onChange={(e) => setCollegeFilter(e.target.value)}
                  >
                    <option value="All">Faculty: All</option>
                    {Object.values(faculties).map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col lg={3} xs={12}>
                  <Button
                    variant="light"
                    className="w-100 text-danger fw-bold bg-danger-subtle border-0"
                    onClick={resetFilters}
                  >
                    <RefreshCw size={16} className="me-2" /> Reset Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>

        {/* === Users Content === */}
        <div className="users-list-wrapper px-3 px-md-5 pb-5">
          {loading ? (
            <div className="loading-state">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading users...</p>
            </div>
          ) : users.length > 0 ? (
            <>
              {/* --- Desktop View (Table) --- */}
              <div className="card border-0 shadow-sm rounded-4 d-none d-md-block overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 custom-table">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">User</th>
                        <th>Role & Faculty</th>
                        <th>University ID</th>
                        <th>Status</th>
                        <th className="text-end pe-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user._id}
                          onClick={() =>
                            navigate(`/admin/view-user-profile/${user._id}`)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          <td className="ps-4">
                            <div className="d-flex align-items-center">
                              <div className="avatar-circle me-3">
                                {user?.profileImage?.secure_url ? (
                                  <img
                                    src={user.profileImage.secure_url}
                                    alt=""
                                    className="avatar-img"
                                  />
                                ) : (
                                  user.fullName.charAt(0)
                                )}
                              </div>
                              <div>
                                <div className="fw-bold text-dark">
                                  {user.fullName}
                                </div>
                                <div
                                  className={`small ${user.confirmEmail ? "text-muted" : "text-warning"}`}
                                >
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex flex-column">
                              {/* <Badge
                                bg="light"
                                text="dark"
                                className="border w-fit mb-1"
                              >
                                {user.role}
                              </Badge> */}
                              <span className="small text-muted">
                                {user.faculty}
                              </span>
                            </div>
                          </td>
                          <td className="fw-semibold text-secondary">
                            {user.universtyID}
                          </td>
                          <td>
                            <span
                              className={`status-dot-pill ${user.status === "ACTIVE" ? "active" : "blocked"}`}
                            >
                              {user.status === "ACTIVE" ? "Active" : "Blocked"}
                            </span>
                          </td>
                          <td className="text-end pe-4">
                            <div className="btn-icon-wrapper">
                              <Eye size={18} className="text-primary" />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* --- Mobile View (Cards) --- */}
              <div className="d-md-none d-flex flex-column gap-3">
                {users.map((user) => (
                  <div
                    className="mobile-user-card"
                    key={user._id}
                    onClick={() =>
                      navigate(`/admin/view-user-profile/${user._id}`)
                    }
                  >
                    {/* Card Header */}
                    <div className="card-header-mobile">
                      {/* Wrapper for Avatar + Text to handle truncation correctly */}
                      <div className="user-info-wrapper">
                        <div className="avatar-circle me-0 flex-shrink-0">
                          {user?.profileImage?.secure_url ? (
                            <img
                              src={user.profileImage.secure_url}
                              alt=""
                              className="avatar-img"
                            />
                          ) : (
                            user.fullName.charAt(0)
                          )}
                        </div>
                        <div className="user-details">
                          <h6 title={user.fullName}>{user.fullName}</h6>
                          <small
                            className={`small ${user.confirmEmail ? "text-muted" : "text-warning"}`}
                            title={user.email}
                          >
                            {user.email}
                          </small>
                        </div>
                      </div>

                      {/* Status Badge (Fixed width) */}
                      <div
                        className={`status-badge-mobile ${user.status === "ACTIVE" ? "active" : "blocked"}`}
                      >
                        {user.status}
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="card-body-mobile">
                      <div className="info-cell">
                        <Hash size={14} />
                        <span title={user.universtyID}>
                          #{user.universtyID}
                        </span>
                      </div>
                      <div className="info-cell">
                        <MapPin size={14} />
                        <span title={user.faculty}>{user.faculty}</span>
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="card-footer-mobile">
                      {/* <Badge
                        bg="light"
                        text="dark"
                        className="border fw-normal px-3"
                      >
                        {user.role}
                      </Badge> */}
                      <span className="view-link">
                        View Profile <Eye size={16} className="ms-1" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* --- Pagination --- */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4 mb-5">
                  <Pagination className="custom-pagination shadow-sm rounded-pill bg-white p-1">
                    <Pagination.Prev
                      disabled={pagination.currentPage === 1}
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                    />
                    <span className="px-3 d-flex align-items-center fw-bold text-muted small">
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <Pagination.Next
                      disabled={
                        pagination.currentPage === pagination.totalPages
                      }
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="icon-bg-lg mb-3">
                <Search size={30} className="text-muted" />
              </div>
              <h5 className="text-danger">No Users Found</h5>
              <p className="text-muted small">
                Try adjusting your search or filters.
              </p>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={resetFilters}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UsersManagement;
